<!--
  (c) Copyright 2024 Calabrio, Inc.
  All Rights Reserved. www.calabrio.com LICENSED MATERIALS
  Property of Calabrio, Inc., Minnesota, USA

  No part of this publication may be reproduced, stored or transmitted,
  in any form or by any means (electronic, mechanical, photocopying,
  recording or otherwise) without prior written permission from Calabrio, Inc.
-->

<template>
  <div class="wy-home-experience-automation-panel-conversation-volume-trend" v-loading="loading" element-loading-text="Loading...">
    <wy-exp-and-auto-header :selecteddatePeriodRange="viewByHeader" :graphType="type"  :showChartViewBy="showChartViewBy" ref="panelHeader" :panelHeaderData="pannelData" :controller="module" :panelTitleNoBracket="panelTitle" :panelTitle="panelTitle" />
    <wy-exp-and-auto-tab-section  ref="tabSection" :controller="module" :panelId="panelId" :sectionData="sectionData.lineChartData" :legendData="legendData" />
  </div>
</template>

<script>
  import Vue from 'vue';
  import moment from 'moment';
  import { mapGetters, mapActions } from 'vuex';
  import WyPanelHeader from '../../wy-exp-and-auto-header.vue';
  import WyPanelTabSection from '../../wy-exp-and-auto-tab-section.vue';
  import WyHomeUtils from '../../utils/wy-home-utils.js';
  import _ from 'lodash';

  export default Vue.extend({
    name: 'wy-home-experience-automation-panel-conversation-volume-trend',
    props: ["controller"],
    components: { 
      'wy-exp-and-auto-header': WyPanelHeader,
      'wy-exp-and-auto-tab-section': WyPanelTabSection
    },

    data() {
      return {
        module: this,
        loading: false,
        templateData: {},
        rawData: {},
        pannelData: {},
        showChartViewBy: true,
        startDate: '',
        type: 'line',
        endDate: '',
        sectionData: {},
        viewByHeader: 'Last 3 months',
        lineChartData: {},
        panelId: 'conversationVolLast30mos',
        panelTitle: 'VA Conversation Volume',
        legendData: {},
        dataIndex: 0,
        dateRange: 'week'
      }
    },

    computed: {
      ...mapGetters(['dataModel', 'companyConfig', 'legendDefaultColors']),
    },

    methods: {
      ...mapActions(['fetchConverVolLast3mosChartData', 'prepareExpAndAutoPageViewByData']),

      async changeDatePeriod(sDate, eDate, dateRangeType){
        this.startDate = sDate;
        this.endDate = eDate;
        this.viewByHeader = dateRangeType;
        await this.handleFilterChange()
      },

      async changeDateRange(dateRange) {
        this.dateRange = dateRange;
        let legendOriginColors = [];
        if(this.companyConfig && this.companyConfig.legends && this.companyConfig.legends.legendColors && this.companyConfig.legends.legendColors.length > 0) {
          legendOriginColors = _.uniq(_.concat(this.companyConfig.legends.legendColors, this.legendDefaultColors));
        }
        else {
          legendOriginColors = this.legendDefaultColors
        }
        //Hard code the colours for now:
        legendOriginColors = ["#0C2837", "#5FBBAA", "#4B67A3"];
        let viewByData = await this.prepareExpAndAutoPageViewByData({viewBy: this.dateRange, rawData: this.rawData, templateData: this.templateData, companyConfig: this.companyConfig});
        this.pannelData = await this.chartData(viewByData, this.templateData, legendOriginColors, this.dateRange)
        this.sectionData.lineChartData = this.pannelData.data
        this.renderLegendData();
        setTimeout(() => {
          if(this.$refs.tabSection && this.$refs.tabSection.$refs.tabBottomSection) {
              this.controller.refreshOverviewPanel(this, this.sectionData);
              this.$refs.tabSection.$refs.tabBottomSection.renderLegends();
          }
        }, 300);
      },
      renderLegendData() {
        if(this.sectionData.lineChartData) {
          this.legendData['legends'] = [];
          this.sectionData.lineChartData.forEach(item => {
            this.legendData['legends'].push(item.lineChartData.legends[0])
          })
        }
      },

      initDateRangeRules() {
        let dateRangeTypes = {
        'month': {
            type: 'month',
            label: 'View by month',
            tickformat: '%b %Y',
            duraion: 99999,
            disabled: false
        },
        'week': {
            type: 'week',
            label: 'View by week',
            tickformat: '%b %d %Y',
            duraion: 30,
            disabled: false
        },
        'day': {
            type: 'day',
            label: 'View by day',
            tickformat: '%b %d %Y',
            duraion: 7,
            disabled: false
        }
      }
        let dateStartMoment = moment(this.dataModel.dateStart,'YYYY-MM-DD');
        let dateEndMoment = moment(this.dataModel.dateEnd,'YYYY-MM-DD');
        this.dateRangeDatesDiff = dateEndMoment.diff(dateStartMoment, 'days', true) + 1;
        const sortedDateRangeTypes = Object.values(dateRangeTypes).sort((a, b) => (a.duraion - b.duraion));
        for(const item of sortedDateRangeTypes) {
          if(this.dateRangeDatesDiff < item.duraion) {
            item.disabled = false;
            sortedDateRangeTypes.filter(otherItem => otherItem.type!=item.type).map(otherItem => {
              otherItem.disabled = (otherItem.duraion > item.duraion);
            });
            break;
          }
        }
        let chartViewBy = (this.companyConfig.default || {}).selectedViewBy || "week"

        if(chartViewBy && chartViewBy.dailyWithinDays && this.dateRangeDatesDiff <= chartViewBy.dailyWithinDays) {
          this.dateRange = 'day'
        }
        else if(chartViewBy && chartViewBy.weeklyWithinDays && this.dateRangeDatesDiff <= chartViewBy.weeklyWithinDays) {
          this.dateRange = 'week'
        }
        else if(chartViewBy && chartViewBy.monthlyWithinDays && this.dateRangeDatesDiff <= chartViewBy.monthlyWithinDays) {
          this.dateRange = 'month'
        }
        else if(chartViewBy && chartViewBy.quarterlyWithinDays && this.dateRangeDatesDiff <= chartViewBy.quarterlyWithinDays) {
          this.dateRange = 'quarter'
        }
        else if(chartViewBy && chartViewBy.yearlyWithinDays && this.dateRangeDatesDiff <= chartViewBy.yearlyWithinDays) {
          this.dateRange = 'year'
        }
        else {
          // get default date range type      
          for(const item of sortedDateRangeTypes) {
            if(this.dateRangeDatesDiff < item.duraion) {
              this.dateRange = item.type;
              break;
            }
          }
        }
      },

      async handleFilterChange() {
        // this.overviewData = null
        if((!this.loading && this.dataModel.dateEnd) ||this.dataModel.dateEnd) {
          this.loading = true;
          if(this.$refs.tabSection && this.$refs.tabSection.$refs.lineChart) {
            this.$refs.tabSection.$refs.lineChart.initDateRangeRules()
            this.dateRange = this.$refs.tabSection.$refs.lineChart.selectedDateRange
          }
          else {
            this.initDateRangeRules();
          }
          // DEMO: for demo purpose
          const demoConfigs = this.companyConfig.demoConfigs || {};
          const demoDateRange = demoConfigs[this.viewByHeader];
          if(demoDateRange && demoDateRange.startDate && demoDateRange.endDate) {
            this.startDate = demoDateRange.endDate;
            this.endDate = demoDateRange.startDate;
          }

          this.rawData = await this.fetchConverVolLast3mosChartData([this.controller.selection, this.startDate, this.endDate]);
          if(this.rawData && Object.keys(this.rawData).length > 0 && this.rawData.dailyMetrics && this.rawData.dailyMetrics.length > 0) {  
            this.rawData = {
              'converVolLast3mos': this.rawData.dailyMetrics
            }
            this.templateData = WyHomeUtils.getHomeExpAndAutoConverVolLast3mothsData();
            // this.dateRange = this.controller.setDefaultDateRange();
            let legendOriginColors = [];
            if(this.companyConfig && this.companyConfig.legends && this.companyConfig.legends.legendColors && this.companyConfig.legends.legendColors.length > 0) {
              legendOriginColors = _.uniq(_.concat(this.companyConfig.legends.legendColors, this.legendDefaultColors));
              // legendOriginColors = companyConfig.legends.legendColors;
            }
            else {
              legendOriginColors = this.legendDefaultColors
            }
            //Hard code the colours for now:
            legendOriginColors = ["#0C2837", "#5FBBAA", "#4B67A3"];
            // if(this.$refs.panelHeader)this.$refs.panelHeader.$refs.settingSection.selectedDateRange = this.dateRange;
            // this.sectionData.lineChartData = [];
            let viewByData = await this.prepareExpAndAutoPageViewByData({viewBy: this.dateRange, rawData: this.rawData, templateData: this.templateData, companyConfig: this.companyConfig});
            this.pannelData = await this.chartData(viewByData, this.templateData, legendOriginColors, this.dateRange)
            this.sectionData.lineChartData = this.pannelData.data
            this.loading = false;
            this.renderLegendData();
            setTimeout(() => {
              if(this.$refs.tabSection && this.$refs.tabSection.$refs.tabBottomSection) {
                  this.controller.refreshOverviewPanel(this, this.sectionData);
                  this.$refs.tabSection.$refs.tabBottomSection.renderLegends();
              }
            }, 300);
          }
          else {
            this.loading = false;
            this.pannelData = {};
            this.sectionData = {}; 
          }
        }
      },

      async chartData(data, templateData, originColors, viewBy) {
        templateData.data.forEach((sectionData)=>{
          //reset
          if(sectionData.lineChartData) {
            sectionData.lineChartData.xAxisData = [];
            sectionData.lineChartData.yAxisData = [];
          }
          sectionData.lineChartData.legendColors = originColors;
          data.forEach((item, charDataIndex) => {
            if(data.length == 1) {
              switch (viewBy) {
                case 'day':
                  sectionData.lineChartData.xAxisData[charDataIndex] = moment(item.date,'YYYY-MM-D').format("MMM DD YYYY");
                  break;
                case 'year':
                  sectionData.lineChartData.xAxisData[charDataIndex] = moment(item.date,'YYYY-MM-D').format("YYYY");
                  break;
                default:
                  sectionData.lineChartData.xAxisData[charDataIndex] = moment(item.date,'YYYY-MM-D').format("MMM YYYY");
              }
            }
            else {
              switch (viewBy) {
                case 'day':
                  sectionData.lineChartData.xAxisData[charDataIndex] = item.date;
                  break;
                case 'week':
                  sectionData.lineChartData.xAxisData[charDataIndex] = moment(item.date,'YYYY-MM-D').format("MMM DD YYYY");
                  break;
                case 'month':
                  sectionData.lineChartData.xAxisData[charDataIndex] = moment(item.date,'YYYY-MM-D').format("MMM YYYY");
                  break;
                case 'year':
                  sectionData.lineChartData.xAxisData[charDataIndex] = moment(item.date,'YYYY-MM-D').format("YYYY");
                  break;
                default:
                  sectionData.lineChartData.xAxisData[charDataIndex] = moment(item.date,'YYYY-MM-D').format("MMM YYYY");
              }
            }
            sectionData.lineChartData.yAxisData[charDataIndex] = Math.round(item[sectionData.key] * 100) / 100;
          })
        })
        return templateData;
      }
    },
    async created(){

      await this.handleFilterChange();
      // this.$el.classList.add("full-size");
    }
  });
</script>

<style lang="scss">
  .wy-home-experience-automation-panel-conversation-volume-trend {
    .el-loading-mask {
      // height: 656px !important;
      top: 65px !important;
    }
  }
</style>